/**
* @module Common
* /

/**
 * Devuelve una fecha formateada desde un string ISO de base de datos
 * @param {string} fecha - Fecha en formato ISO
 * @param {string} iso - The iso format
 * @param {boolean} useLocalLang - The iso format
 * @returns {string} Fecha formateada
 */
export const getFechaFromISO = (
  fecha,
  iso = 'es-Es',
  options = { year: 'numeric', month: 'long', day: 'numeric' },
  useLocalLang = false,
) => {
  const userLang = navigator.language || navigator.userLanguage
  const fixed_date = fecha?.includes('-') ? (fecha?.split('-').map((i) => +i) ?? '') : ''
  if (!fixed_date) return fixed_date
  const date = new Date(fixed_date[0], fixed_date[1] - 1, fixed_date[2])
  return date.toLocaleDateString(useLocalLang ? userLang : iso, options)
}

export const DATE_LOCALE_OPTIONS = { year: 'numeric', month: 'long', day: 'numeric' }
/** @type {Intl.DateTimeFormatOptions} */
export const DATETIME_LOCALE_OPTIONS = {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
}

/**
 * This function can transform a date string into a human
 * readable string
 *
 * @param {string} date - The date to transform
 * @param {boolean} [isDateTime] - You will show the datetime
 * @param {string} [iso] - The iso to lacale
 * @param {Intl.DateTimeFormatOptions}  options - The options for the final date
 * @returns {string} Returns a string with a humman readable date
 */
export const getLocaleFromFullDate = (date, isDateTime = true, iso = 'es-Es', options = null) => {
  if (!date) return ''
  const dividedDate =
    date?.includes('-') && date.length <= 9 ? date.split('-').map((i) => +i) : null
  const transformedDate = dividedDate
    ? new Date(dividedDate[0], dividedDate[1] - 1, dividedDate[2])
    : new Date(date)

  return transformedDate.toLocaleString(
    iso,
    options ?? (isDateTime ? DATETIME_LOCALE_OPTIONS : DATE_LOCALE_OPTIONS),
  )
}

/**
 * Formatea una fecha en el formato corto DD/MM/YYYY HH:MM con ceros a la izquierda.
 * Pensada para columnas de listado donde todas las fechas deben tener la misma longitud.
 *
 * @param {string|Date} date - Fecha a formatear
 * @returns {string} Fecha formateada como "17/04/2026 09:14" o cadena vacía si es inválida
 */
export const formatDateTimeShort = (date) => {
  if (!date) return ''
  const d = new Date(date)
  if (isNaN(d)) return ''
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = d.getFullYear()
  const hours = String(d.getHours()).padStart(2, '0')
  const minutes = String(d.getMinutes()).padStart(2, '0')
  return `${day}/${month}/${year} ${hours}:${minutes}`
}

/**
 * Formatea una fecha en el formato largo DD/MM/YYYY HH:MM:SS con ceros a la izquierda.
 * Pensada para columnas de listado donde se requiere precisión de segundos
 * (por ejemplo, fechas de grabación de facturas).
 *
 * @param {string|Date} date - Fecha a formatear
 * @returns {string} Fecha formateada como "17/04/2026 09:14:07" o cadena vacía si es inválida
 */
export const formatDateTimeLong = (date) => {
  if (!date) return ''
  const d = new Date(date)
  if (isNaN(d)) return ''
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = d.getFullYear()
  const hours = String(d.getHours()).padStart(2, '0')
  const minutes = String(d.getMinutes()).padStart(2, '0')
  const seconds = String(d.getSeconds()).padStart(2, '0')
  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`
}

/**
 * @static
 * @param {Object} date
 * @param {string|null} [separator] - The iso to lacale
 * @param {string} [iso] - The iso to lacale
 * @returns {string|null} return date by locale with personalizable separator or null
 */
export const getLocaleDateString = (dateInput, separator = null, iso = 'es-Es') => {
  if (!dateInput) return null // No hay fecha
  const date = new Date(dateInput)
  if (isNaN(date)) return null // Fecha inválida

  const formatter = new Intl.DateTimeFormat(iso, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })

  const parts = formatter.formatToParts(date)

  // Obtener el separador por defecto si no se proporciona
  const separatorStr = separator ?? parts.find((p) => p.type === 'literal')?.value ?? '/'

  return parts
    .filter((p) => p.type !== 'literal')
    .map((p) => p.value)
    .join(separatorStr)
}
